import { useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { buttonVariants } from "@/components/ui/button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { proxyImageUrl } from "@/lib/snipeit/client";
import type { SnipeItConsumable } from "@/lib/snipeit/types";
import { useConsumablesPage } from "@/common/api/use-consumables-page";
import { useQuickCheckoutConsumable } from "@/common/api/use-checkout-consumable";
import { SortIcon } from "@/components/sort-icon";
import { Pagination } from "@/components/pagination";
import { Search, Loader2, Minus, PackageMinus, PackagePlus } from "lucide-react";
import { Link } from "wouter";

type SortKey = "name" | "category";
type SortDir = "asc" | "desc";

const PAGE_SIZE = 50;

export function ConsumablesTableView() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(0);

  const quickCheckoutMutation = useQuickCheckoutConsumable();

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  }

  const { data, isLoading, isFetching } = useConsumablesPage({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    sort: sortKey,
    order: sortDir,
    search: debouncedSearch || undefined,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <main className="bg-background flex min-h-dvh flex-col overflow-y-auto px-4 pt-6 md:h-dvh md:overflow-hidden md:px-6">
      <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col gap-4">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Suchen…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            className="pl-9"
          />
        </div>

        <div className="border-border bg-card min-h-0 flex-1 overflow-hidden rounded-xl border shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead
                  className="hover:text-foreground cursor-pointer select-none"
                  onClick={() => toggleSort("name")}
                >
                  Artikel
                  <SortIcon col="name" sortKey={sortKey} sortDir={sortDir} />
                </TableHead>
                <TableHead
                  className="hover:text-foreground cursor-pointer select-none"
                  onClick={() => toggleSort("category")}
                >
                  Kategorie
                  <SortIcon col="category" sortKey={sortKey} sortDir={sortDir} />
                </TableHead>
                <TableHead className="text-right">Minimal</TableHead>
                <TableHead className="text-right">Bestand</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                    Laden…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                    Keine Artikel gefunden.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((item: SnipeItConsumable) => {
                const imageUrl = proxyImageUrl(item.image);
                return (
                  <TableRow key={item.id}>
                    <TableCell className="text-foreground font-medium">
                      <div className="flex items-center gap-3">
                        {imageUrl ? (
                          <img src={imageUrl} alt={item.name} className="h-8 w-8 shrink-0 rounded object-contain" />
                        ) : (
                          <div className="bg-muted h-8 w-8 shrink-0 rounded" />
                        )}
                        {item.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{item.category?.name ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-right">{item.min_amt ?? 0}</TableCell>
                    <TableCell className="text-right">
                      <span
                        className={`font-medium ${
                          item.qty < (item.min_amt ?? 0)
                            ? "text-destructive"
                            : item.qty <= (item.min_amt ?? 0) + 5
                              ? "text-amber-500"
                              : "text-foreground"
                        }`}
                      >
                        {item.qty}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="icon"
                          variant="secondary"
                          title="1 ausbuchen"
                          disabled={item.qty < 1 || quickCheckoutMutation.isPending}
                          onClick={() => quickCheckoutMutation.mutate({ consumableId: item.id, currentQty: item.qty })}
                        >
                          {quickCheckoutMutation.isPending &&
                          quickCheckoutMutation.variables?.consumableId === item.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Minus className="h-4 w-4" />
                          )}
                        </Button>
                        <Link
                          href={`/checkout/consumable?id=${item.id}`}
                          className={buttonVariants({ size: "icon", variant: "secondary" })}
                          title="Ausbuchen"
                        >
                          <PackageMinus className="h-4 w-4" />
                        </Link>
                        <Link
                          href={`/checkin/consumable?id=${item.id}`}
                          className={buttonVariants({ size: "icon" })}
                          title="Einbuchen"
                        >
                          <PackagePlus className="h-4 w-4" />
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <Pagination page={page} totalPages={totalPages} isFetching={isFetching} onPageChange={setPage} />

        <div className="pb-4">
          <Link href="/" className={buttonVariants({ variant: "secondary", size: "sm" })}>
            Zurück
          </Link>
        </div>
      </div>
    </main>
  );
}
